/**
 * Carga perezosa de tesseract.js en la página PÚBLICA de validación: el motor
 * (WASM) solo se baja cuando el autor abre el modal del DNI — cero bytes para
 * el resto de la app. Script CDN pineado con SRI; el worker se memoiza.
 *
 * Follow-up documentado: si se quiere eliminar el CDN, copiar dist/ y los
 * traineddata a public/ y apuntar acá (self-hosting).
 */

const TESSERACT_SRC = "https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js";

/**
 * Modelo entrenado sobre el alfabeto del MRZ: OCR-B y los 37 caracteres
 * (A-Z, 0-9 y '<'). Licencia BSD-3, 1,4 MB, se cachea en el navegador.
 *
 * Por qué hace falta: el modelo `eng` no conoce el '<' ni la tipografía OCR-B, y
 * los devuelve como K, L, S o Y. Eso es lo que nos rompía los nombres y los
 * rellenos toda la sesión, y NO se arregla con preproceso — está medido:
 * cambiar el método de umbralado a Sauvola o leer línea por línea con su propio
 * whitelist dieron peor.
 *
 * Con el modelo entrenado, el dorso que daba 2 de 4 dígitos de control y
 * "ODETTE LCLLLLLLLLLLLL" pasa a 4 de 4 y "ODETTE".
 */
const MRZ_LANG_PATH = "https://cdn.jsdelivr.net/gh/DoubangoTelecom/tesseractMRZ@master/tessdata_fast";
const TESSERACT_SRI = "sha384-GJqSu7vueQ9qN0E9yLPb3Wtpd7OrgK8KmYzC8T1IysG1bcvxvIO4qtYR/D3A991F";

export interface OcrWord {
  text: string;
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

/** Caja de un carácter. Es lo que permite medir la inclinación fina del MRZ. */
export interface OcrSymbol {
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

export interface OcrResult {
  text: string;
  words: OcrWord[];
  symbols: OcrSymbol[];
}

export interface OcrEngine {
  recognize(
    image: Blob | HTMLCanvasElement,
    options?: { whitelist?: string; psm?: string }
  ): Promise<OcrResult>;
}

declare global {
  interface Window {
    Tesseract?: {
      createWorker: (lang: string, oem?: number, opts?: object) => Promise<any>;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let enginePromise: Promise<OcrEngine> | null = null;
let mrzEnginePromise: Promise<OcrEngine> | null = null;

function loadScript(): Promise<void> {
  if (window.Tesseract) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = TESSERACT_SRC;
      s.integrity = TESSERACT_SRI;
      s.crossOrigin = "anonymous";
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error("ocr_load_failed"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

function motorCon(
  lang: string,
  opciones?: object
): Promise<OcrEngine> {
  return (async () => {
      await loadScript();
      const worker = await window.Tesseract!.createWorker(lang, 1, opciones);
      return {
        async recognize(
          image: Blob | HTMLCanvasElement,
          options?: { whitelist?: string; psm?: string }
        ) {
          await worker.setParameters({
            tessedit_char_whitelist: options?.whitelist ?? "",
            // El modo de segmentación tiene que ser el mismo que usa el banco
            // de node, o lo que se prueba desde la línea de comandos no es lo
            // que corre acá. "6" es un bloque de texto uniforme, que es lo que
            // parece el MRZ.
            tessedit_pageseg_mode: options?.psm ?? "3",
          });
          // Se piden los símbolos: sin ellos no hay cajas de carácter, y sin
          // esas cajas no se puede medir la inclinación fina del MRZ.
          const { data } = await worker.recognize(image, {}, {
            blocks: true, words: true, symbols: true,
          });
          const words: OcrWord[] = (data.words ?? []).map((w: any) => ({
            text: String(w.text ?? ""),
            bbox: w.bbox ?? { x0: 0, y0: 0, x1: 0, y1: 0 },
          }));
          const symbols: OcrSymbol[] = [];
          for (const b of data.blocks ?? [])
            for (const p of b.paragraphs ?? [])
              for (const l of p.lines ?? [])
                for (const w of l.words ?? [])
                  for (const s of w.symbols ?? []) symbols.push({ bbox: s.bbox });
          return { text: String(data.text ?? ""), words, symbols };
        },
      };
  })();
}

export function loadTesseractEngine(): Promise<OcrEngine> {
  if (!enginePromise) {
    enginePromise = motorCon("eng");
    enginePromise.catch(() => {
      enginePromise = null;
    });
  }
  return enginePromise;
}

/**
 * El motor con el modelo del MRZ. Es opcional: si el CDN no responde, la
 * lectura sigue con el motor común y se pierde calidad, no funcionalidad.
 */
export function loadMrzEngine(): Promise<OcrEngine> {
  if (!mrzEnginePromise) {
    mrzEnginePromise = motorCon("mrz", { langPath: MRZ_LANG_PATH, gzip: false });
    mrzEnginePromise.catch(() => {
      mrzEnginePromise = null;
    });
  }
  return mrzEnginePromise;
}

export const MRZ_WHITELIST = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<";
